import React, { useEffect, useId, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

export interface LienMenuPublic {
  label: string;
  /** Chemin interne (« /blog », « /home#tarifs ») ou ancre de la page courante (« #tarifs »). */
  to: string;
}

interface MenuMobilePublicProps {
  liens: LienMenuPublic[];
}

/**
 * Menu repliable des pages publiques, visible sous 900 px — là où `.landing-nav-links` est masqué.
 * Sans lui, Tarifs, Publications, Outils et Centre d'aide étaient inaccessibles depuis l'en-tête
 * sur mobile (audit du 2026-10-01, U01 : 63 % des sessions de /home sont mobiles).
 */
const MenuMobilePublic: React.FC<MenuMobilePublicProps> = ({ liens }) => {
  const [ouvert, setOuvert] = useState(false);
  const idPanneau = useId();
  const location = useLocation();

  // Changer de page ou d'ancre referme le menu.
  useEffect(() => {
    setOuvert(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!ouvert) return undefined;
    const surTouche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOuvert(false);
    };
    document.addEventListener('keydown', surTouche);
    return () => document.removeEventListener('keydown', surTouche);
  }, [ouvert]);

  return (
    <>
      <button
        type="button"
        className="landing-nav-burger"
        aria-label={ouvert ? 'Fermer le menu' : 'Ouvrir le menu'}
        aria-expanded={ouvert}
        aria-controls={idPanneau}
        onClick={() => setOuvert((v) => !v)}
      >
        {ouvert ? <X size={22} /> : <Menu size={22} />}
      </button>

      {ouvert && (
        <div id={idPanneau} className="landing-nav-panneau">
          <ul>
            {liens.map((lien) => (
              <li key={lien.to}>
                {lien.to.startsWith('#') ? (
                  <a href={lien.to} onClick={() => setOuvert(false)}>{lien.label}</a>
                ) : (
                  <Link to={lien.to} onClick={() => setOuvert(false)}>{lien.label}</Link>
                )}
              </li>
            ))}
            {/* Sous 400 px le bouton « Se connecter » quitte la barre : il doit rester atteignable ici. */}
            <li>
              <Link to="/login" onClick={() => setOuvert(false)}>Se connecter</Link>
            </li>
          </ul>
        </div>
      )}
    </>
  );
};

export default MenuMobilePublic;
